import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FlaskConical, User, Mail, Lock, ChevronRight, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import api from '../../services/api';
import AddressAutocomplete from '../../components/common/AddressAutocomplete';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || (window.location.protocol === 'https:' ? `${window.location.protocol}//${window.location.host}` : `http://${window.location.hostname}:8000`);

const formatPhone = (value) => {
  const digits = (value || '').replace(/\D/g, '').slice(0, 10);
  if (!digits) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

export default function RegisterPage() {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', password: '', confirm_password: '',
    date_of_birth: '', gender: '', race: '', ethnicity: '',
    phone: '', address_line_1: '', city: '', state: '', postal_code: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [serverCode, setServerCode] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [verifyError, setVerifyError] = useState('');
  const [sendingCode, setSendingCode] = useState(false);
  const [tenantInfo, setTenantInfo] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    axios.get(`${API}/api/v1/domains/current`).then(r => {
      if (r.data?.company) setTenantInfo(r.data.company);
    }).catch(() => {});
  }, []);

  const u = (field, val) => setForm(f => ({ ...f, [field]: val }));
  const isValidEmail = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
  const canProceed1 = form.first_name && form.last_name && form.email && isValidEmail(form.email) && emailVerified && form.date_of_birth && form.gender;
  const canProceed2 = form.password && form.password === form.confirm_password && form.password.length >= 6;

  const companyName = tenantInfo?.legal_name || tenantInfo?.dba_name || 'Enterprise LIS';

  const sendVerificationCode = async () => {
    if (!isValidEmail(form.email)) return;
    setSendingCode(true); setVerifyError('');
    try {
      const res = await api.post('/auth/send-verification', { email: form.email });
      const code = res.data.code || '';
      setServerCode(code);
      if (res.data.email_sent === false) {
        setVerificationCode(code);
        setEmailVerified(true);
      } else {
        setEmailSent(true);
      }
    } catch (err) {
      setVerifyError(err?.response?.data?.detail || 'Failed to send code');
    }
    setSendingCode(false);
  };

  const verifyCode = () => {
    if (verificationCode === serverCode) {
      setEmailVerified(true); setVerifyError('');
    } else {
      setVerifyError('Invalid code. Please try again.');
    }
  };

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      await api.post('/auth/register-patient', {
        ...form, username: form.email,
        phone: form.phone.replace(/\D/g, ''),
        full_name: `${form.first_name} ${form.last_name}`.trim(),
      });
      try {
        const loginRes = await api.post('/auth/login', { username: form.email, password: form.password });
        localStorage.setItem('token', loginRes.data.access_token);
        // A patient's home is the portal, and the Dashboard is a page their
        // role cannot view — so this sent every newly registered patient
        // straight to Access Restricted. `/` resolves to where they belong.
        window.location.href = '/';
        return;
      } catch {
        setSuccess(true);
        setTimeout(() => navigate('/login'), 2000);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Registration failed. Please try again.');
    }
    setLoading(false);
  };

  // Styles using CSS variables for dark mode
  const inp = {
    width: '100%', padding: '10px 14px', border: '1px solid var(--input-border, #d1d5db)',
    borderRadius: 10, fontSize: 14, background: 'var(--input-bg, #fff)',
    outline: 'none', color: 'var(--text-primary, #111827)', transition: 'border-color 0.15s',
  };
  const lbl = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary, #6b7280)', display: 'block', marginBottom: 4 };
  const sel = { ...inp, appearance: 'auto' };
  const btnPrimary = {
    width: '100%', padding: 12, border: 'none', borderRadius: 10,
    background: '#2563eb', color: '#fff', cursor: 'pointer',
    fontSize: 14, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    transition: 'opacity 0.15s',
  };
  const btnSecondary = {
    ...btnPrimary, background: 'var(--card-bg, #fff)', color: 'var(--text-secondary, #6b7280)',
    border: '1px solid var(--input-border, #d1d5db)',
  };

  if (success) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: 'var(--page-bg, #f5f5f7)' }}>
        <div style={{ textAlign: 'center', maxWidth: 400, padding: 32, background: 'var(--card-bg, #fff)', borderRadius: 20, border: '1px solid var(--divider, #e5e7eb)', boxShadow: '0 20px 60px rgba(0,0,0,0.08)' }}>
          <CheckCircle size={48} color="#10b981" style={{ marginBottom: 16 }} />
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary)' }}>Registration Successful!</div>
          <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Your account has been created. Redirecting to login...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', padding: 20, background: 'var(--page-bg, #f5f5f7)' }}>
      <div style={{ width: '100%', maxWidth: 520, background: 'var(--card-bg, #fff)', borderRadius: 20, border: '1px solid var(--divider, #e5e7eb)', boxShadow: '0 20px 60px rgba(0,0,0,0.08)', overflow: 'hidden' }}>

        {/* Header */}
        <div style={{ padding: '24px 28px 16px', textAlign: 'center' }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(37,99,235,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', border: '1px solid rgba(37,99,235,0.2)' }}>
            <FlaskConical size={26} color="#2563eb" />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--text-primary, #111827)' }}>Patient Registration</div>
          <div style={{ fontSize: 13, color: 'var(--text-secondary, #6b7280)', marginTop: 4 }}>
            {tenantInfo ? `Create your account with ${companyName}` : 'Create your account to access lab results and services'}
          </div>
        </div>

        {/* Info Banner */}
        <div style={{ margin: '0 28px 16px', padding: '10px 14px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', fontSize: 12, color: 'var(--text-secondary, #4b5563)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
          <AlertCircle size={16} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />
          <span>Your demographics (DOB, sex, race) are required for accurate lab testing and result interpretation.</span>
        </div>

        {/* Step Indicator */}
        <div style={{ display: 'flex', margin: '0 28px 16px', gap: 0 }}>
          {[{ n: 1, label: 'Demographics' }, { n: 2, label: 'Contact' }, { n: 3, label: 'Account' }].map(s => (
            <div key={s.n} style={{ flex: 1, textAlign: 'center', padding: '8px 0', borderBottom: step === s.n ? '2px solid #2563eb' : `2px solid var(--divider, #e5e7eb)`, transition: 'all 0.15s' }}>
              <span style={{ fontSize: 11, fontWeight: step === s.n ? 700 : 500, color: step === s.n ? '#2563eb' : step > s.n ? '#10b981' : 'var(--text-secondary, #94a3b8)' }}>
                {step > s.n ? '✓ ' : ''}{s.label}
              </span>
            </div>
          ))}
        </div>

        {/* Form */}
        <div style={{ padding: '0 28px 24px' }}>

          {/* Step 1: Demographics */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={lbl}>First Name *</label><input value={form.first_name} onChange={e => u('first_name', e.target.value)} placeholder="First name" style={inp} /></div>
                <div><label style={lbl}>Last Name *</label><input value={form.last_name} onChange={e => u('last_name', e.target.value)} placeholder="Last name" style={inp} /></div>
              </div>
              <div>
                <label style={lbl}>Email * <span style={{ fontWeight: 400, color: 'var(--text-secondary)' }}>(this will be your username)</span></label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="email" value={form.email} onChange={e => { u('email', e.target.value); setEmailVerified(false); setEmailSent(false); }}
                    placeholder="your@email.com" style={{ ...inp, flex: 1 }} disabled={emailVerified} />
                  {!emailVerified && (
                    <button onClick={sendVerificationCode} disabled={!isValidEmail(form.email) || sendingCode}
                      style={{ padding: '10px 14px', border: '1.5px solid #2563eb', borderRadius: 10, background: 'transparent', color: '#2563eb', cursor: 'pointer', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap', opacity: (!isValidEmail(form.email) || sendingCode) ? 0.5 : 1 }}>
                      {sendingCode ? 'Sending...' : emailSent ? 'Resend' : 'Verify'}
                    </button>
                  )}
                  {emailVerified && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#10b981', fontSize: 12, fontWeight: 700 }}>
                      <CheckCircle size={16} /> Verified
                    </div>
                  )}
                </div>
                {emailSent && !emailVerified && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4 }}>Enter the 6-digit code sent to {form.email}</div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={verificationCode} onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="000000" maxLength={6}
                        style={{ ...inp, flex: 1, letterSpacing: 4, textAlign: 'center', fontWeight: 700, fontSize: 18 }} />
                      <button onClick={verifyCode} disabled={verificationCode.length !== 6}
                        style={{ padding: '10px 16px', border: '1.5px solid #10b981', borderRadius: 10, background: 'transparent', color: '#10b981', cursor: 'pointer', fontSize: 12, fontWeight: 700, opacity: verificationCode.length === 6 ? 1 : 0.5 }}>
                        Confirm
                      </button>
                    </div>
                    {verifyError && <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{verifyError}</div>}
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={lbl}>Date of Birth *</label><input type="date" value={form.date_of_birth} onChange={e => u('date_of_birth', e.target.value)} style={inp} /></div>
                <div><label style={lbl}>Sex *</label>
                  <select value={form.gender} onChange={e => u('gender', e.target.value)} style={sel}>
                    <option value="">Select</option><option value="M">Male</option><option value="F">Female</option><option value="O">Other</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={lbl}>Race</label>
                  <select value={form.race} onChange={e => u('race', e.target.value)} style={sel}>
                    <option value="">Select</option>
                    <option value="White">White</option><option value="Black or African American">Black or African American</option>
                    <option value="Asian">Asian</option><option value="American Indian or Alaska Native">American Indian / Alaska Native</option>
                    <option value="Native Hawaiian or Other Pacific Islander">Native Hawaiian / Pacific Islander</option>
                    <option value="Two or More Races">Two or More Races</option><option value="Other">Other</option>
                    <option value="Decline to answer">Decline to answer</option>
                  </select>
                </div>
                <div><label style={lbl}>Ethnicity</label>
                  <select value={form.ethnicity} onChange={e => u('ethnicity', e.target.value)} style={sel}>
                    <option value="">Select</option>
                    <option value="Hispanic or Latino">Hispanic or Latino</option>
                    <option value="Not Hispanic or Latino">Not Hispanic or Latino</option>
                    <option value="Decline to answer">Decline to answer</option>
                  </select>
                </div>
              </div>
              <button onClick={() => setStep(2)} disabled={!canProceed1} style={{ ...btnPrimary, marginTop: 8, opacity: canProceed1 ? 1 : 0.5 }}>
                Continue <ChevronRight size={16} />
              </button>
            </div>
          )}

          {/* Step 2: Contact */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div><label style={lbl}>Phone</label><input value={formatPhone(form.phone)} onChange={e => u('phone', formatPhone(e.target.value))} placeholder="(555) 123-4567" style={inp} /></div>
              <div>
                <label style={lbl}>Address</label>
                <AddressAutocomplete initialValue={form.address_line_1 || ''} placeholder="Start typing your address..."
                  onAddressSelect={(addr) => { u('address_line_1', addr.street_line_1 || addr.formatted_address || ''); u('city', addr.city || ''); u('state', addr.state || ''); u('postal_code', addr.postal_code || addr.zip || ''); }} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div><label style={lbl}>City</label><input value={form.city} onChange={e => u('city', e.target.value)} placeholder="City" style={inp} /></div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div><label style={lbl}>State</label><input value={form.state} onChange={e => u('state', e.target.value)} placeholder="ST" style={inp} /></div>
                  <div><label style={lbl}>ZIP</label><input value={form.postal_code} onChange={e => u('postal_code', e.target.value)} placeholder="ZIP" style={inp} /></div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={() => setStep(1)} style={{ ...btnSecondary, flex: 1 }}>Back</button>
                <button onClick={() => setStep(3)} style={{ ...btnPrimary, flex: 2 }}>Continue <ChevronRight size={16} /></button>
              </div>
            </div>
          )}

          {/* Step 3: Account */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(37,99,235,0.06)', border: '1px solid rgba(37,99,235,0.15)', fontSize: 12, color: 'var(--text-secondary)' }}>
                Your login username will be: <strong style={{ color: '#2563eb' }}>{form.email}</strong>
              </div>
              <div><label style={lbl}>Password *</label><input type="password" value={form.password} onChange={e => u('password', e.target.value)} placeholder="Min 6 characters" style={inp} /></div>
              <div>
                <label style={lbl}>Confirm Password *</label>
                <input type="password" value={form.confirm_password} onChange={e => u('confirm_password', e.target.value)} placeholder="Re-enter password" style={inp} />
                {form.confirm_password && form.password !== form.confirm_password && (
                  <div style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>Passwords do not match</div>
                )}
              </div>
              {error && (
                <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', color: '#ef4444', fontSize: 12, fontWeight: 600 }}>
                  {typeof error === 'string' ? error : JSON.stringify(error)}
                </div>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={() => setStep(2)} style={{ ...btnSecondary, flex: 1 }}>Back</button>
                <button onClick={handleSubmit} disabled={!canProceed2 || loading} style={{ ...btnPrimary, flex: 2, background: '#10b981', opacity: (canProceed2 && !loading) ? 1 : 0.5 }}>
                  {loading ? <><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Creating...</> : 'Create Account'}
                </button>
              </div>
            </div>
          )}

          {/* Login link */}
          <div style={{ textAlign: 'center', marginTop: 20, fontSize: 13, color: 'var(--text-secondary)' }}>
            Already have an account?{' '}
            <a href="/login" style={{ color: '#2563eb', fontWeight: 600, textDecoration: 'none' }}>Sign in</a>
          </div>
        </div>
      </div>
    </div>
  );
}
